<template>
  <DialogRoot :open="open" @update:open="$emit('update:open', $event)">
    <DialogTrigger v-if="$slots.trigger" as-child><slot name="trigger"></slot></DialogTrigger>
    <DialogPortal :to="uiPortalTarget" :disabled="inline" :force-mount="frame || undefined">
      <DialogOverlay class="uiDialog__overlay" />
      <!-- No description part: the body is free-form, so there is nothing
           reliable to point aria-describedby at. -->
      <DialogContent
        v-if="frame"
        v-bind="$attrs"
        :id="contentId"
        :as="contentAs"
        :open="contentAs === 'dialog' ? '' : undefined"
        class="uiDialog__frame"
        :aria-describedby="undefined"
      >
        <div class="uiDialog" :class="['uiDialog--' + size, boxClass]">
          <DialogTitle as="h2" class="uiDialog__title"
            ><slot name="title">{{ title }}</slot></DialogTitle
          >
          <slot></slot>
          <DialogClose
            v-if="closeButton"
            class="borderless round uiDialog__close"
            :class="closeClass"
            aria-label="Fechar"
          >
            <IconX color="#000" stroke="2" size="24" />
          </DialogClose>
          <div class="uiDialog__actions uiDialog__actions--framed" :class="actionsClass">
            <slot name="actions" v-if="!hideActions"
              ><DialogClose class="uiDialog__ok">OK</DialogClose></slot
            >
          </div>
        </div>
      </DialogContent>
      <DialogContent
        v-else
        v-bind="$attrs"
        class="uiDialog"
        :class="'uiDialog--' + size"
        :aria-describedby="undefined"
      >
        <DialogTitle as="h2" class="uiDialog__title"
          ><slot name="title">{{ title }}</slot></DialogTitle
        >
        <slot></slot>
        <DialogClose
          v-if="closeButton"
          class="borderless round uiDialog__close"
          aria-label="Fechar"
        >
          <IconX stroke="2" size="24" />
        </DialogClose>
        <div class="uiDialog__actions" v-if="!hideActions">
          <slot name="actions"><DialogClose>OK</DialogClose></slot>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<script>
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from 'reka-ui'
import { IconX } from '@tabler/icons-vue'

import { UI_PORTAL_TARGET, injectUiPortalTarget } from './portalTarget'

export default {
  name: 'UiDialog',
  components: {
    DialogClose,
    DialogContent,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    DialogTrigger,
    IconX,
  },
  inheritAttrs: false,
  inject: { ...injectUiPortalTarget },
  // A framed dialog is where overlays opened from inside it teleport to: put
  // anywhere else, a modal dialog makes them unreachable (pointer events and
  // focus are held inside it). The frame has no transform, so their fixed
  // positioning still counts from the viewport.
  provide() {
    return this.frame ? { [UI_PORTAL_TARGET]: '#' + this.contentId } : {}
  },
  emits: ['update:open'],
  props: {
    // `undefined` leaves Reka in charge (the trigger slot opens it).
    open: { type: Boolean, default: undefined },
    title: { type: String, required: false },
    size: { type: String, default: 'medium' }, // medium | large
    hideActions: { type: Boolean, default: false },
    closeButton: { type: Boolean, default: false },
    // Frame: the dialog element is a full-screen, see-through frame that centres
    // the box by layout (on whole pixels), with the box inside it — the shape
    // of the native <dialog> OverlayWindow was. In a frame the row of actions
    // is always there, empty with hideActions, and the content is in the
    // document by the time the owner's mounted() runs (Reka otherwise mounts
    // a portal one tick late), since owners look for their elements there.
    frame: { type: Boolean, default: false },
    // Stay where the component stands instead of teleporting: inside the call
    // site's own elements, where its CSS and the tests' locators look for it.
    inline: { type: Boolean, default: false },
    // The element the frame is. A `dialog` keeps `dialog.…` selectors working;
    // it carries `open`, but is never modal (never in the top layer): Reka does
    // the modal part.
    contentAs: { type: String, default: 'div' },
    // Extra class(es) for the box, the row of actions and the close button
    // (frame only).
    boxClass: { type: [String, Array, Object], required: false },
    actionsClass: { type: [String, Array, Object], required: false },
    closeClass: { type: [String, Array, Object], required: false },
  },
  data() {
    return {
      contentId: 'ui-dialog-' + crypto.randomUUID(),
    }
  },
}
</script>

<style lang="scss">
@use './layers';

// Same surface as OverlayWindow/OverlayWindow.vue, which this replaces in step 3.
.uiDialog__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: layers.$overlay;
}

.uiDialog {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border: 2px solid var(--primary);
  padding: calc(var(--sp) * 3);
  text-align: center;
  max-height: calc(100vh - var(--sp) * 4);
  overflow-y: auto;
  z-index: layers.$dialog;
}
// The framed form: the frame covers the viewport and centres the box in it.
.uiDialog__frame {
  position: fixed;
  inset: 0;
  z-index: layers.$dialog;
  display: flex;
  justify-content: center;
  align-items: center;
  // A <dialog> frame brings the browser's own dialog box styles.
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  margin: 0;
  border: none;
  padding: 0;
  background: transparent;
  overflow: visible;
  .uiDialog {
    position: relative;
    top: auto;
    left: auto;
    transform: none;
    z-index: auto;
  }
}
.uiDialog--medium {
  max-width: 60em;
}
.uiDialog--large {
  width: calc(100% - var(--sp) * 2);
  text-align: left;
}

.uiDialog__actions {
  margin-top: calc(var(--sp) * 3);
  margin-bottom: calc(var(--sp) * 3);
  text-align: center;
  &:not(.uiDialog__actions--framed) button,
  .uiDialog__ok {
    min-width: 8em !important;
  }
}
// In a frame only its own «OK» is widened, never the buttons put in the actions
// slot: OverlayWindow's rule was scoped, so it never reached them.

.uiDialog__close {
  // Doubled on purpose: `.button.round` / `.button.small` (two classes) would
  // otherwise out-rank a single class on every button that is not a first child.
  &#{&},
  &#{&}:active {
    position: absolute;
    top: var(--sp);
    right: var(--sp);
    min-width: 30px;
    min-height: 30px;
    padding: 0;
    margin: 0;
  }
  svg {
    margin-top: 2px;
    margin-left: -4px;
    stroke: var(--primary);
  }
  &:hover svg {
    stroke: white;
  }
}
</style>
